// ════════════════════════════════════════════════════════════════════════
//  Der Testlauf – tippen und dabei die Zeit nehmen
//
//  Die Engine bleibt zeitlos (kein Timer, kein React), das ist ihre Zusage. Die
//  Uhr steht deshalb hier, und sie startet beim ERSTEN RICHTIGEN ANSCHLAG:
//  Nicht beim Anzeigen – sonst misst man mit, wie lange ein Kind die Anleitung
//  liest, und das misst man bei einem schwachen Leser zweimal.
//
//  Gestoppt wird beim letzten Zeichen. Was danach passiert (Ergebnis lesen,
//  weiterklicken), gehört nicht in die Messung.
// ════════════════════════════════════════════════════════════════════════

import { useCallback, useMemo, useRef, useState } from "react";
import { accuracy, startStage, target, typeKey, type StageState } from "@otto/lehrgang";

export type Measurement = {
  /** Richtig getippte Zeichen. Sie sind die Leistung, nicht die Anschläge. */
  readonly chars: number;
  /** Ganze Sekunden, mindestens 1: eine Messung ohne Zeit gibt es nicht. */
  readonly seconds: number;
  readonly accuracy: number;
};

export type Assessment = {
  readonly state: StageState;
  readonly expected: string | null;
  readonly started: boolean;
  readonly done: Measurement | null;
  readonly missCount: number;
  readonly handleKey: (key: string) => void;
};

export function useAssessment(
  text: string,
  now: () => number = () => performance.now(),
): Assessment {
  const stage = useMemo(() => ({ key: "assessment", title: "", hint: "", text }), [text]);
  const [state, setState] = useState<StageState>(() => startStage(stage));
  const [done, setDone] = useState<Measurement | null>(null);
  const startedAt = useRef<number | null>(null);

  const handleKey = useCallback(
    (key: string) => {
      setState((before) => {
        if (before.complete) return before;

        const { state: after, stroke } = typeKey(before, key);
        if (stroke === "ignored") return before;

        // Die Uhr läuft ab dem ersten Treffer. Ein Fehlgriff davor - ein Kind,
        // das die Tastatur sucht - startet sie nicht: gemessen wird das Tippen,
        // nicht das Zögern davor.
        if (startedAt.current === null && stroke === "hit") startedAt.current = now();

        if (after.complete && startedAt.current !== null) {
          const seconds = Math.max(1, Math.round((now() - startedAt.current) / 1000));
          setDone({ chars: after.correct, seconds, accuracy: accuracy(after) });
        }
        return after;
      });
    },
    [now],
  );

  return {
    state,
    expected: target(state),
    started: startedAt.current !== null,
    done,
    missCount: state.errors,
    handleKey,
  };
}
